import { useRef, useState } from 'react';
import {
  Box, Button, Flex, IconButton, Text,
} from '@chakra-ui/react';
import {
  LuArrowRight, LuCheck, LuChevronRight, LuEqual, LuTriangleAlert, LuUndo2, LuX,
} from 'react-icons/lu';
import { Trans, useTranslation } from 'react-i18next';
import { formatPhoneMoney } from '../../../utils/formatters';
import PhoneSheet, { usePhoneSheet } from '../../phone/PhoneSheet';
import { OPEN_MOVEMENT_EVENT } from '../../phone/events';
import { movementLabel } from './CheckupParts';
import { CheckupSheetFooter, FooterNote, PhonePill } from './PhoneCheckupSheet';

const AMOUNT = { in: 'phone.income', out: 'redesign.amount.expense' };
const GOOD_TINT = { bg: 'rgba(21, 128, 61, 0.10)', _dark: { bg: 'rgba(74, 222, 128, 0.14)' } };

// The facts the check carries of a movement, by id.
const factsOf = (item, id) => (item.movements || []).find((movement) => movement.id === id) || null;

// "−COP 42,300": a reading as the phone prints money, its direction as the sign.
const phoneMoney = (side, currency) => (side?.amount == null
  ? '—'
  : formatPhoneMoney(currency, side.direction === 'out' ? -Math.abs(side.amount) : Math.abs(side.amount), { signed: true }));

// A round icon on a tint: something to fix, or something that stays as it is.
function Disc({ tone, icon }) {
  const good = tone === 'good';
  return (
    <Flex
      w="32px"
      h="32px"
      borderRadius="full"
      align="center"
      justify="center"
      flexShrink={0}
      {...(good ? GOOD_TINT : { bg: 'phone.alertBg' })}
      color={good ? 'phone.income' : 'phone.alert'}
    >
      <Box as={icon} boxSize="18px" aria-hidden="true" />
    </Flex>
  );
}

function Card({ children, ...rest }) {
  return (
    <Box bg="redesign.bg.surface" border="1px solid" borderColor="redesign.border" borderRadius="14px" overflow="hidden" {...rest}>
      {children}
    </Box>
  );
}

function Caption({ children, ...rest }) {
  return <Text textStyle="phoneCaption" color="phone.muted" {...rest}>{children}</Text>;
}

// One fact of a card: its icon, what it is about, and its value on the right.
function Fact({ tone, icon, name, sub, children }) {
  return (
    <Flex align="center" gap="12px" minH="48px" px="16px" py="8px">
      <Disc tone={tone} icon={icon} />
      <Box flex="1" minW={0}>
        <Text textStyle="phoneRowName" color="redesign.heading">{name}</Text>
        {sub && <Text textStyle="phoneMeta" color="phone.muted">{sub}</Text>}
      </Box>
      <Box textStyle="phoneRowAmount" flexShrink={0} whiteSpace="nowrap">{children}</Box>
    </Flex>
  );
}

const Divider = () => <Box h="1px" bg="redesign.border" ml="60px" />;

// One reading of the example's bank row: today's, which stays, or the
// earlier one, which goes.
function Reading({ side, currency, keep, sub }) {
  const { t } = useTranslation('repairs');
  return (
    <Flex gap="12px" py="10px" borderTop="1px solid" borderColor="redesign.border">
      <Disc tone={keep ? 'good' : 'alert'} icon={keep ? LuCheck : LuX} />
      <Box flex="1" minW={0}>
        <Flex align="center" gap="8px" wrap="wrap">
          <Text
            textStyle="phoneRowAmount"
            whiteSpace="nowrap"
            color={keep ? AMOUNT[side?.direction] || 'redesign.heading' : 'phone.muted'}
            textDecoration={keep ? undefined : 'line-through'}
          >
            {phoneMoney(side, currency)}
          </Text>
          <PhonePill tone={keep ? 'good' : 'alert'}>{t(keep ? 'checkup.readTwice.stays' : 'checkup.readTwice.goes')}</PhonePill>
        </Flex>
        <Text textStyle="phoneMeta" color="phone.muted">{sub}</Text>
      </Box>
    </Flex>
  );
}

// One movement with an extra reading, worked through: the reading that stays
// and the one that goes, each beside its label.
function Example({ item, details, names }) {
  const { t } = useTranslation('repairs');
  const change = item.change || {};
  const facts = factsOf(item, change.movement_id);
  const currency = change.currency || facts?.currency;
  const { title, meta } = movementLabel(details[change.movement_id], facts, names, t);
  const kept = change.after?.direction;
  const extra = change.before?.direction;
  let extraSub = 'checkup.readTwice.phone.repeated';
  if (kept && extra && kept !== extra) {
    extraSub = extra === 'in' ? 'checkup.readTwice.phone.flippedIn' : 'checkup.readTwice.phone.flippedOut';
  }
  return (
    <Card px="16px" pt="12px" pb="2px">
      <Caption>{t('checkup.readTwice.phone.example')}</Caption>
      <Flex mt="8px" mb="8px" align="baseline" justify="space-between" gap="12px" minW={0}>
        <Text textStyle="phoneRowName" color="redesign.heading" noOfLines={1} minW={0}>{title}</Text>
        <Text textStyle="phoneMeta" color="phone.muted" flexShrink={0} whiteSpace="nowrap">{meta}</Text>
      </Flex>
      <Reading
        side={change.after}
        currency={currency}
        keep
        sub={t(kept === 'in' ? 'checkup.readTwice.phone.keptIn' : 'checkup.readTwice.phone.keptOut')}
      />
      <Reading side={change.before} currency={currency} sub={t(extraSub)} />
    </Card>
  );
}

// The list sheet's ✕.
function SheetClose() {
  const { t } = useTranslation('common');
  const { close } = usePhoneSheet();
  return (
    <IconButton
      icon={<LuX size={22} />}
      variant="ghost"
      borderRadius="full"
      color="redesign.text"
      minW="44px"
      minH="44px"
      aria-label={t('actions.close')}
      onClick={() => close()}
    />
  );
}

// The list sheet's button: the sheet goes first, then the fix is applied.
function ListApply({ label, onApply, applying }) {
  const { close } = usePhoneSheet();
  const leaving = useRef(false);
  return (
    <Button
      variant="phonePrimary"
      w="100%"
      isLoading={applying}
      onClick={async () => {
        if (leaving.current) return;
        leaving.current = true;
        await close();
        onApply();
      }}
    >
      {label}
    </Button>
  );
}

// The phone shell opens a movement's sheet over everything, this sheet too.
const openMovement = (id) => window.dispatchEvent(new CustomEvent(OPEN_MOVEMENT_EVENT, { detail: { id } }));

// One movement of the list: it stays, with today's reading; the extra one
// goes. Opens the movement.
function ListRow({ item, details, names, onOpen }) {
  const { t } = useTranslation('repairs');
  const change = item.change || {};
  const id = change.movement_id;
  const facts = factsOf(item, id);
  const currency = change.currency || facts?.currency;
  const { title, meta } = movementLabel(details[id], facts, names, t);
  const kept = phoneMoney(change.after, currency);
  const removed = phoneMoney(change.before, currency);
  return (
    <Box
      as="button"
      type="button"
      onClick={() => onOpen(id)}
      disabled={id == null}
      display="flex"
      alignItems="center"
      gap="12px"
      w="100%"
      minH="68px"
      pl="16px"
      pr="8px"
      py="10px"
      textAlign="left"
      borderTop="1px solid"
      borderColor="redesign.border"
      _active={{ bg: 'redesign.bg.hover' }}
      aria-label={t('checkup.readTwice.phone.rowLabel', {
        name: title, meta, kept, removed,
      })}
    >
      <Box flex="1" minW={0}>
        <Text textStyle="phoneRowName" color="redesign.heading" noOfLines={1}>{title}</Text>
        <Text textStyle="phoneMeta" color="phone.muted" noOfLines={1}>{meta}</Text>
      </Box>
      <Box flexShrink={0} textAlign="right">
        <Flex align="center" justify="flex-end" gap="4px">
          <Box as={LuCheck} boxSize="14px" color="phone.income" aria-hidden="true" />
          <Text textStyle="phoneRowAmount" whiteSpace="nowrap" color={AMOUNT[change.after?.direction] || 'redesign.heading'}>{kept}</Text>
        </Flex>
        <Flex align="center" justify="flex-end" gap="4px">
          <Box as={LuX} boxSize="12px" color="phone.alert" aria-hidden="true" />
          <Text textStyle="phoneMeta" whiteSpace="nowrap" color="phone.muted" textDecoration="line-through">{removed}</Text>
        </Flex>
      </Box>
      <Box as={LuChevronRight} boxSize="20px" color="phone.muted" flexShrink={0} aria-hidden="true" />
    </Box>
  );
}

/**
 * Every movement with an extra reading, in a sheet over the review: the
 * reading that stays and the one that goes, a row each. A row opens its
 * movement; the button removes them all, as the review's does.
 */
function ListSheet({
  isOpen, onClose, items, sameCount, details, names, applyLabel, onApply, applying,
}) {
  const { t } = useTranslation('repairs');
  const count = items.length;
  return (
    <PhoneSheet
      id="read-twice-list"
      isOpen={isOpen}
      onClose={onClose}
      size="content"
      title={count === 1 ? t('checkup.readTwice.phone.listOne') : t('checkup.readTwice.phone.listOther', { count })}
      headerRight={<SheetClose />}
      footer={(
        <>
          <ListApply label={applyLabel} onApply={onApply} applying={applying} />
          <FooterNote>{t('checkup.footer.undoAfter')}</FooterNote>
        </>
      )}
    >
      <Flex gap="16px" px="16px" pb="10px" textStyle="phoneMeta" color="phone.muted">
        <Flex align="center" gap="4px">
          <Box as={LuCheck} boxSize="14px" color="phone.income" aria-hidden="true" />
          {t('checkup.readTwice.stays')}
        </Flex>
        <Flex align="center" gap="4px">
          <Box as={LuX} boxSize="14px" color="phone.alert" aria-hidden="true" />
          {t('checkup.readTwice.goes')}
        </Flex>
      </Flex>
      {items.map((item) => (
        <ListRow key={item.id} item={item} details={details} names={names} onOpen={openMovement} />
      ))}
      {sameCount > 0 && (
        <Box mx="16px" mt="12px" px="14px" py="12px" borderRadius="12px" bg="redesign.bg.card">
          <Text textStyle="phoneMeta" color="redesign.text">
            {t(sameCount === 1 ? 'checkup.readTwice.phone.sameOne' : 'checkup.readTwice.phone.sameOther', { count: sameCount })}
          </Text>
        </Box>
      )}
    </PhoneSheet>
  );
}

/**
 * A statement read twice, on the phone: what happened and that the
 * movements are fine, what the fix changes (warnings) and what it does not
 * (the balance, the movements), one movement worked through, and one button
 * in the sheet's footer. The full list opens in a sheet.
 */
export function ReadTwicePhoneReview({
  readTwice, sameCount, details, names, metaLine, onApplyAll, applying,
}) {
  const { t } = useTranslation('repairs');
  const [listOpen, setListOpen] = useState(false);
  const { items, warnings } = readTwice;
  const count = items.length;
  const apply = () => onApplyAll(items);
  const applyLabel = count === 1 ? t('checkup.readTwice.applyOne') : t('checkup.readTwice.phone.applyOther', { count });
  const subKey = warnings > 0
    ? (warnings === 1 ? 'checkup.readTwice.phone.subWarningsOne' : 'checkup.readTwice.phone.subWarningsOther')
    : (count === 1 ? 'checkup.readTwice.phone.subOne' : 'checkup.readTwice.phone.subOther');

  return (
    <>
      <Flex direction="column" gap="14px" pb="16px">
        <Box>
          <Text as="h2" fontSize="22px" lineHeight="28px" fontWeight="700" color="redesign.heading" sx={{ textWrap: 'balance' }}>
            {t('checkup.readTwice.phone.title')}
          </Text>
          <Text mt="6px" textStyle="phoneBody" color="redesign.text">
            <Trans
              t={t}
              i18nKey={subKey}
              values={{ count: warnings > 0 ? warnings : count }}
              components={{ strong: <Box as="strong" fontWeight="600" color="redesign.heading" /> }}
            />
          </Text>
          {metaLine && <Text mt="4px" textStyle="phoneMeta" color="phone.muted" overflowWrap="anywhere">{metaLine}</Text>}
        </Box>

        <Card>
          <Caption px="16px" pt="12px">{t('checkup.readTwice.phone.ifFixed')}</Caption>
          {warnings > 0 && (
            <>
              <Fact tone="alert" icon={LuTriangleAlert} name={t('checkup.readTwice.phone.warnings')}>
                <Flex
                  align="center"
                  gap="6px"
                  role="img"
                  aria-label={t('checkup.readTwice.phone.fromTo', { from: warnings, to: 0 })}
                  sx={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  <Box as="span" color="phone.muted">{warnings}</Box>
                  <Box as={LuArrowRight} boxSize="16px" color="phone.muted" />
                  <Box as="span" color="phone.income">0</Box>
                </Flex>
              </Fact>
              <Divider />
            </>
          )}
          <Fact tone="good" icon={LuEqual} name={t('checkup.readTwice.tiles.balance')}>
            <Box as="span" color="phone.income">{t('checkup.readTwice.phone.balanceSame')}</Box>
          </Fact>
          <Divider />
          <Fact tone="good" icon={LuEqual} name={t('checkup.readTwice.tiles.movements')} sub={t('checkup.readTwice.phone.movementsSub')}>
            <Box as="span" color="phone.income">{t('checkup.readTwice.phone.movementsSame')}</Box>
          </Fact>
          <Box
            as="button"
            type="button"
            aria-haspopup="dialog"
            onClick={() => setListOpen(true)}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap="12px"
            w="100%"
            minH="48px"
            pl="16px"
            pr="12px"
            borderTop="1px solid"
            borderColor="redesign.border"
            color="phone.link"
            fontSize="16px"
            lineHeight="22px"
            fontWeight="600"
            textAlign="left"
            _active={{ bg: 'redesign.bg.hover' }}
          >
            {t(count === 1 ? 'checkup.readTwice.seeAllOne' : 'checkup.readTwice.seeAllOther', { count })}
            <Box as={LuChevronRight} boxSize="20px" aria-hidden="true" />
          </Box>
        </Card>

        {items[0] && <Example item={items[0]} details={details} names={names} />}
      </Flex>

      <CheckupSheetFooter>
        <Button variant="phonePrimary" w="100%" onClick={apply} isLoading={applying}>{applyLabel}</Button>
        <FooterNote>{t('checkup.footer.undoAfter')}</FooterNote>
      </CheckupSheetFooter>

      <ListSheet
        isOpen={listOpen}
        onClose={() => setListOpen(false)}
        items={items}
        sameCount={sameCount}
        details={details}
        names={names}
        applyLabel={applyLabel}
        onApply={apply}
        applying={applying}
      />
    </>
  );
}

/**
 * A statement read twice, once fixed: it is clean, what went (the extra
 * readings and their warnings) and what did not change, and Undo.
 */
export function ReadTwicePhoneDone({ summary, onUndo, undoing }) {
  const { t } = useTranslation('repairs');
  const count = summary.applied.length;
  const warnings = summary.warningsCleared;
  const plural = count === 1 ? 'One' : 'Other';
  return (
    <Flex direction="column" gap="16px" pt="8px" pb="16px">
      <Flex direction="column" align="center" textAlign="center">
        <Flex w="64px" h="64px" borderRadius="full" align="center" justify="center" {...GOOD_TINT} color="phone.income">
          <Box as={LuCheck} boxSize="30px" aria-hidden="true" />
        </Flex>
        <Text as="h2" mt="14px" fontSize="22px" lineHeight="28px" fontWeight="700" color="redesign.heading">
          {t('checkup.readTwice.phone.doneTitle')}
        </Text>
        <Text mt="6px" textStyle="phoneBody" color="redesign.text" sx={{ textWrap: 'balance' }}>
          {t(`checkup.readTwice.phone.${warnings > 0 ? 'doneSubWarnings' : 'doneSub'}${plural}`, { count })}
        </Text>
      </Flex>

      <Card>
        {warnings > 0 && (
          <>
            <Fact tone="good" icon={LuCheck} name={t('checkup.readTwice.phone.warningsCleared')}>
              <Box as="span" color="redesign.heading" sx={{ fontVariantNumeric: 'tabular-nums' }}>{warnings}</Box>
            </Fact>
            <Divider />
          </>
        )}
        <Fact tone="good" icon={LuEqual} name={t('checkup.readTwice.phone.movementsChanged')}>
          <Box as="span" color="redesign.heading">0</Box>
        </Fact>
        <Divider />
        <Fact tone="good" icon={LuEqual} name={t('checkup.readTwice.phone.balance')}>
          <Box as="span" color="phone.income">{t('checkup.readTwice.phone.balanceKept')}</Box>
        </Fact>
      </Card>

      <Flex align="center" justify="space-between" gap="12px">
        <Text textStyle="phoneMeta" color="phone.muted">{t('checkup.readTwice.phone.changedMind')}</Text>
        <Button
          variant="outline"
          minH="44px"
          px="16px"
          borderRadius="12px"
          borderColor="redesign.border"
          bg="redesign.bg.surface"
          color="phone.link"
          fontSize="16px"
          fontWeight="600"
          leftIcon={<LuUndo2 />}
          onClick={onUndo}
          isLoading={undoing}
        >
          {t('checkup.done.undo')}
        </Button>
      </Flex>
    </Flex>
  );
}
